import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useFloatingChromeSuppressed } from "@/lib/chrome-policy";

const STORAGE_KEY = "ka_cookie_consent_v1";

type Consent = "all" | "essential";

export function getCookieConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === "all" || value === "essential" ? value : null;
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  // On chamber routes the bottom edge belongs to the composer, so the notice
  // moves to the top rather than covering the primary control.
  const chamber = useFloatingChromeSuppressed();

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  const decide = (choice: Consent) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {}
    setVisible(false);
    window.dispatchEvent(new CustomEvent("ka:cookie-consent", { detail: choice }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      className={`fixed inset-x-3 z-50 mx-auto max-w-3xl rounded-xl border hairline bg-charcoal/95 p-4 shadow-2xl backdrop-blur sm:p-5 ${chamber ? "top-20" : "bottom-3"}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          We use essential cookies to run the site, plus optional analytics and marketing cookies to improve it.{" "}
          <Link to="/cookies" className="text-gold underline">Learn more</Link>.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => decide("essential")}
            className="rounded-full border hairline px-4 py-2 text-xs font-medium hover:bg-obsidian"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => decide("all")}
            className="rounded-full bg-gold-gradient px-4 py-2 text-xs font-semibold text-obsidian"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
